import React, { useState } from 'react';
import '../styles/PlanChangeModal.css';

// Types
interface ProductTier {
  id: string;
  name: string;
  description: string;
  price_id: string;
  amount: number;
  currency: string;
  interval: string;
  features: string[];
  metadata: Record<string, string>;
}

interface UserSubscription {
  subscription_id: string;
  stripe_product_id: string;
  stripe_price_id: string;
  status: string;
  current_period_end: string;
}

interface PlanChangeModalProps {
  currentTier: ProductTier | null;
  newTier: ProductTier;
  currentSubscription: UserSubscription;
  onConfirm: () => void;
  onCancel: () => void;
  onError: (error: string) => void;
}

const PlanChangeModal: React.FC<PlanChangeModalProps> = ({
  currentTier,
  newTier,
  currentSubscription,
  onConfirm,
  onCancel,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch('/api/change-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          new_price_id: newTier.price_id,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to change plan');
      }

      onConfirm();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to change plan';
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const isUpgrade = newTier.amount > (currentTier?.amount || 0);
  const changeType = isUpgrade ? 'Upgrade' : 'Downgrade';

  return (
    <div className="plan-change-modal">
      <div className="plan-change-content">
        <div className="plan-change-header">
          <h3>{changeType} Your Plan</h3>
          <button 
            type="button" 
            className="btn-close" 
            aria-label="Close"
            onClick={onCancel}
          />
        </div>

        <div className="plan-change-summary">
          <div className="plan-comparison">
            <div className="current-plan">
              <h4>Current Plan</h4>
              <div className="plan-details">
                <h5>{currentTier?.name || 'Unknown'}</h5>
                <p className="plan-price">
                  {currentTier ? formatPrice(currentTier.amount, currentTier.currency) : 'N/A'}
                  /{currentTier?.interval || 'month'}
                </p>
              </div>
            </div>

            <div className="arrow">
              <span>→</span>
            </div>

            <div className="new-plan">
              <h4>New Plan</h4>
              <div className="plan-details">
                <h5>{newTier.name}</h5>
                <p className="plan-price">
                  {formatPrice(newTier.amount, newTier.currency)}/{newTier.interval}
                </p>
              </div>
            </div>
          </div>

          <div className="billing-info">
            <div className="alert alert-info">
              <h6>Billing Information</h6>
              {isUpgrade ? (
                <p>
                  You'll be charged a prorated amount for the {changeType.toLowerCase()} today. 
                  Your next billing cycle will be {formatPrice(newTier.amount, newTier.currency)} on{' '}
                  {new Date(currentSubscription.current_period_end).toLocaleDateString()}.
                </p>
              ) : (
                <p>
                  You'll receive a prorated credit for the {changeType.toLowerCase()}. 
                  Your next billing cycle will be {formatPrice(newTier.amount, newTier.currency)} on{' '}
                  {new Date(currentSubscription.current_period_end).toLocaleDateString()}.
                </p>
              )}
            </div>
          </div>

          <div className="features-comparison">
            <h6>New Plan Features</h6>
            <ul className="feature-list">
              {newTier.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="plan-change-actions">
          <button 
            type="button" 
            className="btn btn-secondary me-2" 
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Processing...
              </>
            ) : (
              `Confirm ${changeType}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanChangeModal;
